import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { fileUrl } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { PostJobDialog } from "./PostJobDialog";
import { ReviewDialog } from "./ReviewDialog";
import { StarRating } from "./StarRating";
import { jobStatusLabel, jobStatusColor } from "../lib/constants";
import { CheckCircle2, ShieldCheck, Truck, MapPin } from "lucide-react";

function JobRow({ job, onChange }) {
  const [apps, setApps] = useState(null);
  const [open, setOpen] = useState(false);

  const loadApps = async () => {
    const r = await api.get(`/transport-jobs/${job.job_id}/applications`);
    setApps(r.data);
    setOpen(true);
  };
  const accept = async (appId) => {
    await api.post(`/transport-jobs/${job.job_id}/applications/${appId}/accept`);
    toast.success("Transporter assigned");
    onChange();
  };
  const confirm = async () => {
    try {
      await api.post(`/transport-jobs/${job.job_id}/confirm`);
      toast.success("Job completed — you can now review the transporter");
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const pod = job.proof_of_delivery;

  return (
    <div className="p-4" data-testid={`job-${job.job_id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium">{job.title}</span>
        <Badge className={`rounded-none text-[10px] uppercase ${jobStatusColor(job.status)}`} data-testid={`job-badge-${job.job_id}`}>{jobStatusLabel(job)}</Badge>
      </div>
      <p className="text-sm text-muted-foreground mt-1">{job.pickup_location} → {job.delivery_location}{job.truck_type ? ` · ${job.truck_type}` : ""}{job.request_quote ? " · quote" : job.price ? ` · £${job.price}` : ""}</p>
      {job.driver_name && (
        <p className="text-xs text-primary mt-1">
          Transporter: <Link to={`/transporter/${job.driver_id}`} className="underline" data-testid={`job-transporter-link-${job.job_id}`}>{job.driver_name}</Link>
        </p>
      )}
      {pod && (
        <div className="mt-2 bg-muted p-2 text-xs flex items-center gap-3" data-testid={`pod-${job.job_id}`}>
          <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
          <span>Proof of delivery: {pod.note || "—"}</span>
          {pod.photo && <a href={fileUrl(pod.photo)} target="_blank" rel="noopener noreferrer" className="text-primary underline">photo</a>}
        </div>
      )}
      <div className="flex gap-2 mt-3 flex-wrap">
        <Button size="sm" variant="outline" className="rounded-none" onClick={loadApps} data-testid={`view-applicants-${job.job_id}`}>View applicants ({job.applications_count || 0})</Button>
        {job.status === "delivered" && <Button size="sm" className="rounded-none bg-primary hover:bg-primary/90" onClick={confirm} data-testid={`confirm-job-${job.job_id}`}>Confirm completion</Button>}
        {job.status === "completed" && <ReviewDialog jobId={job.job_id} transporterName={job.driver_name} onDone={onChange} />}
      </div>
      {open && apps && (
        <div className="mt-3 border-t border-border pt-3 space-y-2" data-testid={`applicants-${job.job_id}`}>
          {apps.length === 0 ? <p className="text-sm text-muted-foreground">No applicants yet.</p> : apps.map((a) => (
            <div key={a.application_id} className="flex items-start justify-between gap-3 bg-muted p-3" data-testid={`applicant-${a.application_id}`}>
              <div className="min-w-0">
                <div className="font-medium text-sm flex items-center gap-2 flex-wrap">
                  <Link to={`/transporter/${a.driver_id}`} className="underline">{a.driver_name}</Link>
                  {a.transporter_verified && (
                    <Badge className="rounded-none bg-accent text-foreground text-[9px] uppercase tracking-wider" data-testid={`applicant-verified-${a.application_id}`}>
                      <ShieldCheck className="h-3 w-3 mr-0.5" /> Verified
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-1.5 text-xs mt-1" data-testid={`applicant-rating-${a.application_id}`}>
                  {a.transporter_rating != null ? (
                    <><StarRating value={a.transporter_rating} size="h-3 w-3" /><span className="font-medium">{a.transporter_rating}</span><span className="text-muted-foreground">· {a.transporter_reviews || 0} review{a.transporter_reviews === 1 ? "" : "s"}</span></>
                  ) : <span className="text-muted-foreground">No rating yet</span>}
                  <span className="text-muted-foreground">· {a.transporter_completed || 0} completed</span>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground mt-1">
                  <span className="inline-flex items-center gap-1"><Truck className="h-3 w-3" /> {a.driver_vehicle_type || "—"}</span>
                  {a.driver_service_areas && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {a.driver_service_areas}</span>}
                </div>
                <div className="text-xs text-muted-foreground mt-1">Quote: <strong className="text-foreground">{a.quote ? `£${a.quote}` : "—"}</strong>{a.message ? ` · ${a.message}` : ""}{a.contact ? ` · ${a.contact}` : ""}</div>
              </div>
              <div className="shrink-0">
                {a.status === "accepted" ? <Badge className="rounded-none bg-primary text-white text-[10px] uppercase">assigned</Badge>
                  : job.status === "open" ? <Button size="sm" className="rounded-none bg-primary hover:bg-primary/90" onClick={() => accept(a.application_id)} data-testid={`accept-applicant-${a.application_id}`}>Assign</Button>
                  : <Badge className="rounded-none bg-muted text-[10px] uppercase">{a.status}</Badge>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export const PostedJobsManager = ({ disabled }) => {
  const [jobs, setJobs] = useState([]);
  const reload = useCallback(async () => {
    const r = await api.get("/seller/transport-jobs");
    setJobs(r.data);
  }, []);
  useEffect(() => { reload(); }, [reload]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <p className="text-sm text-muted-foreground">Post any transport job — verified drivers apply with quotes.</p>
        <PostJobDialog disabled={disabled} onSaved={reload} />
      </div>
      {jobs.length === 0 ? (
        <div className="border border-dashed border-border p-16 text-center text-muted-foreground" data-testid="empty-state">No transport jobs posted yet.</div>
      ) : (
        <div className="border border-border divide-y divide-border">
          {jobs.map((j) => <JobRow key={j.job_id} job={j} onChange={reload} />)}
        </div>
      )}
    </div>
  );
};
